import { Box, Chip, Divider, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { taskStatusToChipSx } from "components/Tasks/statusStyles";
import { TaskDeadline } from "components/Tasks/TaskDeadline";
import { taskStatusToLabel } from "components/Tasks/util.labels";
import { VirtualEventStatus } from "generated-graphql/graphql";
import { Fragment } from "react";

import { useTaskCalendarPreview } from "./useTaskCalendarPreview";

type CalendarPreviewListProps = ReturnType<typeof useTaskCalendarPreview>;

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * How much a calendar feed will sync, and its first few items by deadline,
 * styled like the task list. Leads with tasks by series and with events by
 * date, matching the task list's layout.
 */
export function CalendarPreviewList({
  loading,
  bySeries,
  taskCount,
  eventCount,
  items,
}: CalendarPreviewListProps) {
  const theme = useTheme();
  const total = bySeries ? taskCount : eventCount;
  const moreCount = total === null ? 0 : total - items.length;

  const renderCounts = () => {
    if (taskCount === null || eventCount === null) {
      return <Skeleton width={180} />;
    }
    const tasks = plural(taskCount, "task");
    const events = plural(eventCount, "event");
    return (
      <Typography variant="body2">
        <strong>{bySeries ? tasks : events}</strong> ({bySeries ? events : tasks}) will sync
      </Typography>
    );
  };

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Box sx={{ px: 1.5, py: 1, bgcolor: "action.hover" }}>{renderCounts()}</Box>
      {items.length === 0 ? (
        <>
          <Divider />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 1.5, py: 1 }}
          >
            {loading ? "Loading…" : "No tasks will sync."}
          </Typography>
        </>
      ) : (
        items.map((item) => (
          <Fragment key={item.id}>
            <Divider />
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 0.75 }}>
              <Box sx={{ width: 112, flexShrink: 0 }}>
                <TaskDeadline
                  deadline={item.deadline}
                  overdue={item.status === VirtualEventStatus.Overdue}
                  subtitle={bySeries && item.isRecurring ? "Recurring" : undefined}
                />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap>
                  {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {item.facilities?.map((facility) => facility.name).join(", ")}
                </Typography>
              </Box>
              <Chip
                label={taskStatusToLabel(item.taskStatus)}
                size="small"
                sx={taskStatusToChipSx(item.taskStatus, theme)}
              />
            </Stack>
          </Fragment>
        ))
      )}
      {moreCount > 0 && (
        <>
          <Divider />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 1.5, py: 0.75 }}
          >
            + {plural(moreCount, bySeries ? "more task" : "more event")}
          </Typography>
        </>
      )}
    </Box>
  );
}
